import type { LineRow, NoteWithAuthor, SmeCardView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Pill } from "../../../ui/Pill";
import { SpeechMark } from "../../../ui/SpeechMark";
import { StateBadge, displayState } from "../../../ui/StateBadge";
import { lineKindWord } from "../shared/words";
import { anythingWaits, lineReturned, lineWaitsAlone } from "../shared/waits";
import { lineCanBeSentBack } from "./decide";
import { factRows } from "./facts";
import { isLibraryLine, lineTitle, mappingNotes } from "./lineText";

export type LineAnswer = "validate" | "reject" | "send_back" | "note";

interface LineItemProps {
  line: LineRow;
  card: SmeCardView;
  notes: readonly NoteWithAuthor[];
  busy: boolean;
  onAnswer: (answer: LineAnswer, line: LineRow) => void;
}

function LineMarks({ line, alone }: { line: LineRow; alone: boolean }) {
  const look = displayState(line.state, { origin: line.origin });
  return (
    <span className="ra-line__marks row">
      {lineReturned(line) && <Pill tone="sent_back">Line returned</Pill>}
      {alone && !lineReturned(line) && <StateBadge state="waiting" />}
      {!alone && !lineReturned(line) && look && look !== "waiting" && <StateBadge state={look} />}
      {line.pending?.status === "waiting" && <Pill tone="waiting">Next version of this line waits</Pill>}
      {line.removal?.status === "waiting" && <Pill tone="waiting">Marked for removal</Pill>}
      {line.origin === "app" && <Pill>Made by the app</Pill>}
    </span>
  );
}

function LineItem({ line, card, notes, busy, onAnswer }: LineItemProps) {
  const alone = lineWaitsAlone(line, card.element);
  const sendable = lineCanBeSentBack(line, card.element);
  const canNote = anythingWaits(card.element, [...card.library_lines, ...card.lines]);
  const title = lineTitle(line);
  // The title already says what a short value says (a phase, a code), so the list keeps only what adds to it.
  const details = isLibraryLine(line)
    ? mappingNotes(line)
    : factRows(line.content)
        .filter((row) => !title.includes(row.value))
        .map((row) => `${row.label}: ${row.value}`);
  const mine = notes.filter((note) => note.line_id === line.id);
  return (
    <li className="ra-line" data-state={line.state ?? undefined} data-alone={alone ? "true" : undefined} data-line={line.id}>
      <div className="ra-line__head">
        <span className="muted ra-line__kind">{lineKindWord(line.kind)}</span>
        <strong className="ra-line__title">{title}</strong>
        <LineMarks line={line} alone={alone} />
        {mine.length > 0 && <SpeechMark count={mine.length} label={mine.length === 1 ? "1 note on this line" : `${mine.length} notes on this line`} />}
      </div>
      {details.length > 0 && (
        <ul className="ra-line__details muted">
          {details.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}
      {line.sme_comment && lineReturned(line) && <p className="ra-line__comment">You sent it back: {line.sme_comment}</p>}
      {line.sme_reason && line.state === "rejected" && <p className="ra-line__comment">You rejected it: {line.sme_reason}</p>}
      {mine.map((note) => (
        <p key={note.id} className="ra-note">
          <SpeechMark label="Note" /> <span className="muted">{note.author.display}:</span> {note.text}
        </p>
      ))}
      {(alone || sendable || canNote) && (
        <div className="ra-line__actions row">
          {alone && (
            <Button variant="secondary" disabled={busy || !card.displayed} onClick={() => onAnswer("validate", line)} aria-label={`Validate this line: ${lineTitle(line)}`}>
              Validate this line
            </Button>
          )}
          {alone && (
            <Button variant="quiet" disabled={busy} onClick={() => onAnswer("reject", line)} aria-label={`Reject this line: ${lineTitle(line)}`}>
              Reject this line
            </Button>
          )}
          {sendable && (
            <Button variant="quiet" disabled={busy} onClick={() => onAnswer("send_back", line)} aria-label={`Send back this line: ${lineTitle(line)}`}>
              Send back
            </Button>
          )}
          {canNote && (
            <Button variant="quiet" disabled={busy} onClick={() => onAnswer("note", line)} aria-label={`Add a note on this line: ${lineTitle(line)}`}>
              Note
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

interface LineListProps {
  title: string;
  titleId: string;
  lines: readonly LineRow[];
  card: SmeCardView;
  busy: boolean;
  /** Said when there are none. Left out to draw nothing at all. */
  empty?: string;
  onAnswer: (answer: LineAnswer, line: LineRow) => void;
}

/** The lines and values inside a card, each with its own mark and its own answers (DEC-1016-74). */
export function LineList({ title, titleId, lines, card, busy, empty, onAnswer }: LineListProps) {
  if (lines.length === 0 && !empty) return null;
  return (
    <Card className="ra-section" aria-labelledby={titleId}>
      <h2 id={titleId}>{title}</h2>
      {lines.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul className="ra-lines">
          {lines.map((line) => (
            <LineItem key={line.id} line={line} card={card} notes={card.notes} busy={busy} onAnswer={onAnswer} />
          ))}
        </ul>
      )}
    </Card>
  );
}
